import React, { memo, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import styled from '@emotion/styled'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import { Config } from '@surglogs-app/types'
import { Box, Button } from '@surglogs-app/ui'
import { DefaultFormField, InputField } from '@surglogs-app/ui.form'
import { DialogLoader } from '@surglogs-app/ui.loader'
import { DefaultModal } from '@surglogs-app/ui.modal'
import { isPasswordOk } from '@surglogs-app/util'

import { useAccountEditPasswordSubmitHandler } from '../hooks/useAccountEditPasswordSubmitHandler'
import { useAccountSettingsTranslation } from '../services'

import { AccountSettingsRecaptchaProvider } from './AccountSettingsRecaptchaProvider'
import { PasswordInput } from './PasswordInput'

const FormGroup = styled(Box)`
  margin-bottom: 25px;
`

const validator = yup.object().shape({
  password: yup.string().trim().required('isRequired'),
  newPassword: yup
    .string()
    .trim()
    .required('isRequired')
    .test('password', 'invalid', (value) => isPasswordOk(value)),
  retypeNewPassword: yup
    .string()
    .trim()
    .required('isRequired')
    .when('newPassword', (newPassword: string, schema: yup.StringSchema) =>
      schema.test({
        test: (retypePassword) => retypePassword === newPassword,
        message: 'invalid',
      }),
    ),
})

interface SettingsUserEditPasswordModalProps {
  hideModal: () => void
  config: Pick<Config, 'reCaptchaKey'>
}

interface FormValues {
  password: string
  newPassword: string
  retypeNewPassword: string
}

const AccountEditPasswordModalComponent = memo(
  ({ hideModal, config }: SettingsUserEditPasswordModalProps) => {
    const { t: ct } = useAccountSettingsTranslation()
    const {
      handleSubmit,
      register,
      formState: { isDirty, errors },
      control,
    } = useForm<FormValues>({
      mode: 'onChange',
      reValidateMode: 'onChange',
      resolver: yupResolver(validator),
    })
    const [onSubmit, request] = useAccountEditPasswordSubmitHandler({
      hideModal,
      config,
    })

    const footerChildren = useMemo(
      () => (
        <>
          <Button
            {...{ type: 'submit', disabled: !isDirty, variant: 'success' }}>
            {ct('buttonLabel')}
          </Button>
          <Button {...{ variant: 'secondary', onClick: hideModal }}>
            {ct('revertChanges')}
          </Button>
        </>
      ),
      [ct, hideModal, isDirty],
    )

    return (
      <form {...{ onSubmit: handleSubmit(onSubmit) }}>
        <DefaultModal
          {...{
            title: ct('passwordEditTitle'),
            onCancelButtonClick: null,
            footerChildren,
            hideModal,
          }}>
          {request.isPending ? <DialogLoader /> : null}
          <FormGroup>
            <InputField
              {...{
                ...register('password'),
                errors,
                control,
                type: 'password',
                formFieldProps: { t: ct },
              }}
            />
          </FormGroup>
          <FormGroup>
            <DefaultFormField
              {...{
                name: 'newPassword',
                errors,
                t: ct,
                validation: false,
              }}>
              <PasswordInput {...register('newPassword')} />
            </DefaultFormField>
          </FormGroup>
          <FormGroup>
            <DefaultFormField
              {...{
                name: 'retypeNewPassword',
                errors,
                t: ct,
                validation: false,
              }}>
              <PasswordInput
                {...{ validation: false, ...register('retypeNewPassword') }}
              />
            </DefaultFormField>
          </FormGroup>
        </DefaultModal>
      </form>
    )
  },
)

export const AccountEditPasswordModal = (
  props: SettingsUserEditPasswordModalProps,
) => (
  <AccountSettingsRecaptchaProvider {...{ config: props.config }}>
    <AccountEditPasswordModalComponent {...props} />
  </AccountSettingsRecaptchaProvider>
)
